import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CheckRounded, CloseRounded } from '@mui/icons-material';
import DeckNumberField from './DeckNumberField';
import {
  CODE_VARIABLES,
  EMU_PER_POINT,
  FIELD_ACTIONS,
  FIELD_KINDS,
  FIELD_SOURCES,
  FIT_POLICIES,
  MAX_INSTRUCTION_LENGTH,
  SHAPE_ACTIONS,
  actionOf,
  bboxOf,
  capacityOf,
  emuToInches,
  limitsOverCapacity,
  shapeDisplayName,
} from './deckModel';

/** Limit inputs per field kind. */
const LIMITS_BY_KIND = {
  text: [['maxChars', 'Max characters'], ['maxLines', 'Max lines']],
  rich: [['maxChars', 'Max characters'], ['maxLines', 'Max lines']],
  list: [['maxBullets', 'Max bullets'], ['maxCharsPerBullet', 'Max characters per bullet']],
  table_rows: [['maxRows', 'Max rows']],
};

/**
 * Font size in points from the manifest (hundredths of a point, points, or EMU).
 * @param {unknown} size - Stored size.
 * @returns {number|null} Points.
 */
function pointsOf(size) {
  const n = Number(size);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= EMU_PER_POINT) return Math.round((n / EMU_PER_POINT) * 10) / 10;
  if (n >= 400) return Math.round(n) / 100;
  return n;
}

/**
 * Facts about the selected box: size, font and placeholder.
 * @param {object} props - Props.
 * @param {object} props.shape - Shape.
 * @returns {React.ReactElement}
 */
function ShapeFacts({ shape }) {
  const box = bboxOf(shape);
  const pt = pointsOf(shape.font?.sizePt ?? shape.font?.size);
  const face = shape.font?.face || shape.font?.family || shape.fontFace || '';
  const facts = [
    shape.kind ? `Kind: ${shape.kind}` : null,
    shape.phType ? `Placeholder: ${shape.phType}${shape.phIdx !== undefined && shape.phIdx !== null ? ` ${shape.phIdx}` : ''}` : null,
    box ? `${emuToInches(box.cx)} x ${emuToInches(box.cy)} in` : null,
    face || pt ? `${face}${face && pt ? ', ' : ''}${pt ? `${pt} pt` : ''}` : null,
  ].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {facts.map((fact) => (
        <Chip key={fact} label={fact} size="small" variant="outlined" />
      ))}
    </Box>
  );
}

/**
 * Inspector for one shape: what it does and, for AI fields, the field spec.
 * @param {object} props - Props.
 * @param {object} props.shape - Shape.
 * @param {object|undefined} props.field - Its field (with `id`).
 * @param {number} props.ordinal - 1-based position on the slide.
 * @param {boolean} [props.readOnly] - No edits.
 * @param {function(string): void} props.onAction - Change the action.
 * @param {function(string): void} props.onVariable - Choose a code variable.
 * @param {function(string, object, string=): void} props.onFieldChange - Edit the field (id, patch, coalesce key).
 * @param {function(string): void} props.onAccept - Accept the suggestion.
 * @param {function(string): void} props.onReject - Reject the suggestion.
 * @param {React.Ref} [props.headingRef] - The heading (focusable with tabIndex -1), so focus can
 *   move here when the control that had it goes away.
 * @returns {React.ReactElement}
 */
function ShapeInspector({ shape, field, ordinal, readOnly = false, onAction, onVariable, onFieldChange, onAccept, onReject, headingRef }) {
  const action = actionOf(shape, field);
  const isField = FIELD_ACTIONS.includes(action) && field;
  const suggested = isField && field.state !== 'accepted';
  const capacity = capacityOf(shape);
  const over = isField ? limitsOverCapacity(field, shape) : [];
  const idBase = `deck-shape-${String(shape.key).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const sample = typeof shape.samplePreview === 'string' ? shape.samplePreview.slice(0, 300) : '';
  const confidence = Number(field?.confidence);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="shape-inspector">
      <Box>
        <Typography component="h3" ref={headingRef} tabIndex={-1} data-testid="shape-inspector-heading" sx={{ fontSize: 15, fontWeight: 600, outline: 'none' }}>
          Box {ordinal}: {shapeDisplayName(shape, ordinal)}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <ShapeFacts shape={shape} />
        </Box>
      </Box>

      {shape.ruleReason && (
        <Typography variant="body2" color="text.secondary">
          Set by Quill's rules ({String(shape.ruleReason).replace(/_/g, ' ')}).
        </Typography>
      )}

      {suggested && (
        <Alert
          severity="info"
          icon={false}
          action={!readOnly && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" startIcon={<CheckRounded />} onClick={() => onAccept(field.id)}>Accept</Button>
              <Button size="small" color="inherit" startIcon={<CloseRounded />} onClick={() => onReject(field.id)}>Reject</Button>
            </Box>
          )}
        >
          {field.origin === 'rule' ? 'Suggested by Quill\'s rules.' : 'Suggested by AI.'}
          {Number.isFinite(confidence) && confidence > 0 ? ` Confidence ${Math.round(confidence * (confidence <= 1 ? 100 : 1))}%.` : ''}
        </Alert>
      )}

      <FormControl size="small" fullWidth disabled={readOnly}>
        <InputLabel id={`${idBase}-action-label`}>What this box does</InputLabel>
        <Select
          labelId={`${idBase}-action-label`}
          label="What this box does"
          value={action}
          onChange={(event) => onAction(event.target.value)}
        >
          {SHAPE_ACTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {SHAPE_ACTIONS.find((a) => a.value === action)?.help}
        </Typography>
      </FormControl>

      {action === 'variable' && (
        <FormControl size="small" fullWidth disabled={readOnly}>
          <InputLabel id={`${idBase}-variable-label`}>Code variable</InputLabel>
          <Select
            labelId={`${idBase}-variable-label`}
            label="Code variable"
            value={CODE_VARIABLES.some((v) => v.name === shape.variable) ? shape.variable : ''}
            onChange={(event) => onVariable(event.target.value)}
          >
            {CODE_VARIABLES.map((variable) => (
              <MenuItem key={variable.name} value={variable.name}>
                {variable.label} ({`{{${variable.name}}}`})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isField && (
        <>
          <TextField
            id={`${idBase}-prompt`}
            label="Prompt"
            value={field.instruction || ''}
            onChange={(event) => onFieldChange(field.id, { instruction: event.target.value }, `${field.id}:instruction`)}
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            InputProps={{ readOnly }}
            inputProps={{ maxLength: MAX_INSTRUCTION_LENGTH }}
            helperText={field.instruction ? 'Describe what belongs here, not the old wording.' : 'Say what this box should contain, for example "One sentence on the business outcome".'}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel id={`${idBase}-kind-label`}>Kind</InputLabel>
              <Select labelId={`${idBase}-kind-label`} label="Kind" value={field.kind || 'text'} onChange={(event) => onFieldChange(field.id, { kind: event.target.value })}>
                {FIELD_KINDS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                {!FIELD_KINDS.some((k) => k.value === field.kind) && field.kind && <MenuItem value={field.kind} disabled>{field.kind}</MenuItem>}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth disabled={readOnly}>
              <InputLabel id={`${idBase}-source-label`}>Source</InputLabel>
              <Select labelId={`${idBase}-source-label`} label="Source" value={FIELD_SOURCES.some((s) => s.value === field.source) ? field.source : 'any'} onChange={(event) => onFieldChange(field.id, { source: event.target.value })}>
                {FIELD_SOURCES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <FormControlLabel
            control={<Switch checked={field.required !== false} disabled={readOnly || action === 'optional'} onChange={(event) => onFieldChange(field.id, { required: event.target.checked })} />}
            label={action === 'optional' ? 'Optional: removed when there is nothing to say' : 'Required'}
          />

          <Box component="fieldset" sx={{ border: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography component="legend" sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Limits</Typography>
            {(LIMITS_BY_KIND[field.kind] || LIMITS_BY_KIND.text).map(([key, label]) => {
              const value = field.limits?.[key];
              const cap = capacity[key];
              const tooHigh = over.includes(key);
              let helper = cap ? `Up to ${cap}, what the box holds${capacity.approximate ? ' (estimate)' : ''}.` : 'No measured capacity for this box.';
              if (tooHigh) helper = `Above what the box holds (about ${cap}). Generated text may not fit.`;
              return (
                <DeckNumberField
                  key={key}
                  id={`${idBase}-${key}`}
                  size="small"
                  label={label}
                  value={Number.isFinite(value) ? value : null}
                  min={1}
                  // While typing, the input keeps what is typed; an empty limit falls back to the
                  // box capacity only when the input loses focus.
                  onCommit={(n) => onFieldChange(field.id, { limits: { [key]: n } }, `${field.id}:${key}`)}
                  InputProps={{ readOnly }}
                  inputProps={cap ? { max: cap } : {}}
                  error={tooHigh}
                  helperText={helper}
                />
              );
            })}
          </Box>

          <FormControl size="small" fullWidth disabled={readOnly}>
            <InputLabel id={`${idBase}-fit-label`}>When text does not fit</InputLabel>
            <Select labelId={`${idBase}-fit-label`} label="When text does not fit" value={FIT_POLICIES.some((f) => f.value === field.fit) ? field.fit : 'shrink_then_split'} onChange={(event) => onFieldChange(field.id, { fit: event.target.value })}>
              {FIT_POLICIES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
        </>
      )}

      {sample && (
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>Example text in the master</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: 'italic' }}>
            {sample}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ShapeInspector;
